<template>
  <default-layout :hide-mobile-back-button="true" class="dashboard">
    <main-container size="full">
      <div class="dashboard__wrapper">
        <div class="dashboard__wrapper--aside">
          <dashboard-womp ref="womp" /> 
        </div>
        <main class="dashboard__wrapper--main">
          <dashboard-pinboard ref="pinboard" />
          <dashboard-header
            v-model:search-query="searchQuery"
            :view-type="viewType"
            @update-view-type="changeView"
          />
          <!-- Show projects first, if a user only has those, so they don't get the zero-state at the top -->
          <div v-if="onlyProjectsExist || showDashboardProjectsFirst">
            <dashboard-projects
              :view-type="viewType"
              :is-loading="projectLoading"
              :is-searching="isSearching"
              :projects="projectsToDisplay"
              :daily-activity-logs="projectDailyActivityLogs"
              @project-archived="removeProject"
            />
            <div v-if="showMailboxes" class="dd-divider" />
            <i v-if="!onlyProjectsExist" class="ddi ddi-flip-vertical-down" @click="toggleEntityOrdering()" />
            <dashboard-mailboxes
              v-if="showMailboxes"
              :view-type="viewType"
              :is-loading="mailboxLoading"
              :is-searching="isSearching"
              :mailboxes="mailboxesToDisplay"
              :daily-activity-logs="mailboxDailyActivityLogs"
              class="dashboard__wrapper--main__last-entity-group"
              @mailbox-archived="removeMailbox"
            />
          </div>
          <div v-else>
            <dashboard-mailboxes
              v-if="showMailboxes"
              :view-type="viewType"
              :is-loading="mailboxLoading"
              :is-searching="isSearching"
              :mailboxes="mailboxesToDisplay"
              :daily-activity-logs="mailboxDailyActivityLogs"
              @mailbox-archived="removeMailbox"
            />
            <div v-if="showMailboxes" class="dd-divider" />
            <i v-if="!onlyProjectsExist" class="ddi ddi-flip-vertical-down rotate-180" @click="toggleEntityOrdering()" />
            <dashboard-projects
              :view-type="viewType"
              :is-loading="projectLoading"
              :is-searching="isSearching"
              :projects="projectsToDisplay"
              :daily-activity-logs="projectDailyActivityLogs"
              class="dashboard__wrapper--main__last-entity-group"
              @project-archived="removeProject"
            />
          </div>
          <template v-if="archiveEnabled">
            <div class="dd-divider" />
            <div v-if="isLoaded" class="archived-callout">
              <dd-button size="large" type="text" @click="showArchived=true">
                {{ archiveLabel }}
              </dd-button>
            </div>
          </template>
        </main>
      </div>
    </main-container>
    <dashboard-archived 
      v-model:show-modal="showArchived"
      @mailbox-restored="onMailboxRestored"
      @project-restored="onProjectRestored"
    />
  </default-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useProfileStore } from '@/modules/profile/profile.store'
import DefaultLayout from '@/layouts/default-layout.vue'

/* Mixins */

/* Components */
import MainContainer from '@/layouts/main-container.vue'
import DashboardWomp from './components/dashboard-womp.vue'
import DashboardPinboard from './components/dashboard-pinboard.vue'
import DashboardMailboxes from './views/dashboard-mailboxes.vue'
import DashboardProjects from './views/dashboard-projects.vue'
import DashboardHeader from './views/dashboard-header.vue'
import DashboardArchived from './views/dashboard-archived.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'

/* Data types */
import {
  IDashboardOutputFavorite,
  IProjectDailyActivityLogsDailyActivityLog,
  IProjectsOutputProjectForHome,
} from '@/api/types/response.types'

/* API */
import DashboardAPI from '@/api/modules/dashboard.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import { useAppStore } from '@/modules/app/app.store'

export default defineComponent({
  name: 'DashboardScreen',

  components: {
    DefaultLayout,
    MainContainer,
    DashboardWomp,
    DashboardPinboard,
    DashboardHeader,
    DashboardMailboxes,
    DashboardProjects,
    DashboardArchived,
  },

  data() {
    return {
      desktopViewType: '',
      searchQuery: '',
      favorites: [] as IDashboardOutputFavorite[],
      projectData: [] as IProjectsOutputProjectForHome[],
      mailboxData: [] as IProjectsOutputProjectForHome[],
      projectDailyActivityLogs: [] as IProjectDailyActivityLogsDailyActivityLog[],
      mailboxDailyActivityLogs: [] as IProjectDailyActivityLogsDailyActivityLog[],
      archivedMailboxCount: 0,
      archivedProjectCount: 0,
      mailboxLoading: true,
      projectLoading: true,
      showArchived: false,
      showDashboardProjectsFirst: false,

      /* Polling data */
      lastAccessEpochTime: null as any,
      poller: null as any,
      pollIntervalInMS: useAppStore().autoRefreshDataPingInMS,
      isUnmounted: false,
    }
  },

  head(): any {
    return {
      title: `${this.company.name}`,
    }
  },

  computed: {
    ...mapState(useProfileStore, { company: 'currentCompany' }),
    archiveEnabled(): boolean {
      return (
        !this.$screen.isMobile(this.$mq) &&
        this.$permissions.isAdminOrOwner &&
        (this.archivedMailboxCount > 0 || this.archivedProjectCount > 0)
      )
    },
    archiveLabel(): string {
      let result = 'View archived'
      if (this.archivedMailboxCount > 0) {
        result += ' mailboxes (' + this.archivedMailboxCount + ')'
        if (this.archivedProjectCount > 0) {
          result += ' & '
        }
      }
      if (this.archivedProjectCount > 0) {
        result += ' projects (' + this.archivedProjectCount + ')'
      }

      return result
    },
    /**
     * Filter mailbox list based on entered string
     */
    mailboxesToDisplay(): IProjectsOutputProjectForHome[] {
      if (this.searchQuery !== '') {
        return this.mailboxData.filter((item: any) => {
          return item.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        })
      }
      return this.mailboxData
    },

    /**
     * Filter projects list based on entered string
     */
    projectsToDisplay(): IProjectsOutputProjectForHome[] {
      if (this.searchQuery !== '') {
        return this.projectData.filter((item: any) => {
          return item.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        })
      }
      return this.projectData
    },

    isSearching(): boolean {
      return this.searchQuery !== ''
    },

    isLoaded(): boolean {
      return !this.projectLoading && !this.mailboxLoading
    },

    onlyProjectsExist(): boolean {
      return (
        !this.mailboxLoading &&
        !this.projectLoading &&
        this.mailboxData.length === 0 &&
        this.projectData.length > 0
      )
    },

    onlyMailboxesExist(): boolean {
      return (
        !this.mailboxLoading &&
        !this.projectLoading &&
        this.projectData.length === 0 &&
        this.mailboxData.length > 0
      )
    },

    hasProjects(): boolean {
      return !this.projectLoading && this.projectData.length > 0
    },

    hasMailboxes(): boolean {
      return !this.mailboxLoading && this.mailboxData.length > 0
    },

    showMailboxes(): boolean {
      return this.$permissions.isNotGuest
    },

    viewType(): string {
      if (this.$screen.isTabletOrMobile(this.$mq)) {
        return 'table'
      }
      return this.desktopViewType
    },
  },

  beforeUnmount() {
    this.isUnmounted = true
    this.endPolling()
  },

  mounted() {
    this.updateDesktopViewType()
  },

  async created() {
    try {
      this.showDashboardProjectsFirst = LocalStore.dashboardProjectsFirst
      await this.getDashboardEntityInfo()
      this.startPolling()
    } catch (err) {
      this.$error.emit(err)
    }
  },

  methods: {
    updateDesktopViewType() {
      const cachedViewType = LocalStore.dashboardViewType
      if (['grid', 'table'].includes(cachedViewType)) {
        this.desktopViewType = cachedViewType
      } else {
        this.desktopViewType = 'grid'
      }
    },
    toggleEntityOrdering() {
      this.showDashboardProjectsFirst = !this.showDashboardProjectsFirst
      LocalStore.dashboardProjectsFirst = this.showDashboardProjectsFirst
    },
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await DashboardAPI.get_MostRecentLastUpdatedProjectEpochTime()
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await this.getDashboardEntityInfo(false)
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async getDashboardEntityInfo(showResultsLoading: boolean = true) {
      this.mailboxLoading = showResultsLoading
      this.projectLoading = showResultsLoading

      const response = await DashboardAPI.get_ProjectInfo()
      this.lastAccessEpochTime = response.accessEpochTime
      this.mailboxData = response.listMailboxes
      this.projectData = response.listInternalProjects
      this.archivedMailboxCount = response.archivedMailboxCount
      this.archivedProjectCount = response.archivedInternalProjectCount

      this.mailboxLoading = false
      this.projectLoading = false

      Promise.all([
        this.getMailboxDailyActivityLogs(),
        this.getProjectDailyActivityLogs(),
      ])
    },

    async getMailboxDailyActivityLogs() {
      if (!this.hasMailboxes) {
        return
      }
      /* Return for mobile as it doesn't use activity charts */
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      try {
        const mailboxDailyActivityLogsWrapper = await MailboxesAPI.get_DailyActivityLogs()
        this.mailboxDailyActivityLogs =
          mailboxDailyActivityLogsWrapper.listDailyActivityLogs
      } catch {
        // Do nothing, in case it's a timeout from slow DB...
      }
    },

    async getProjectDailyActivityLogs() {
      if (!this.hasProjects) {
        return
      }
      /* Return for mobile as it doesn't use activity charts */
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      try {
        const projectDailyActivityLogsWrapper = await InternalProjectsAPI.get_DailyActivityLogs()
        this.projectDailyActivityLogs =
          projectDailyActivityLogsWrapper.listDailyActivityLogs
      } catch {
        // Do nothing, in case it's a timeout from slow DB...
      }
    },

    async reloadComponentData() {
      await Promise.all([
        // @ts-ignore
        this.$refs.womp.getWompStats(),
        // @ts-ignore
        this.$refs.pinboard.getPinnedItems(),
      ])
    },

    async onMailboxRestored() {
      this.archivedMailboxCount--
      this.mailboxLoading = true
      this.mailboxData = await MailboxesAPI.get_Index()
      this.mailboxLoading = false
      this.getMailboxDailyActivityLogs()
      await this.reloadComponentData()
    },

    async onProjectRestored() {
      this.archivedProjectCount--
      this.projectLoading = true
      this.projectData = await InternalProjectsAPI.get_Index()
      this.projectLoading = false
      this.getProjectDailyActivityLogs()
      await this.reloadComponentData()
    },

    async removeMailbox(removedMailbox: IProjectsOutputProjectForHome) {
      this.mailboxData = this.mailboxData.filter(
        m => m.id !== removedMailbox.id,
      )
      this.archivedMailboxCount++
      await this.reloadComponentData()
    },

    async removeProject(removedProject: IProjectsOutputProjectForHome) {
      this.projectData = this.projectData.filter(
        m => m.id !== removedProject.id,
      )
      this.archivedProjectCount++
      await this.reloadComponentData()
    },

    changeView(value: string) {
      LocalStore.dashboardViewType = value
      this.updateDesktopViewType()
    },
  },
})
</script>

<style lang="scss">
$sidebar-width-xl: 25%;
$sidebar-width-lg: 35%;

.dashboard {
  &__wrapper {
    display: flex;

    @include media-breakpoint-down(md) {
      flex-direction: column;
      padding: 0 15px;
      margin-bottom: 60px;

      > * {
        margin-bottom: 20px;
      }
    }

    @include media-breakpoint-up(lg) {
      @include flex-row-horizontal-spacing(30px);
    }

    &--aside {
      @include media-breakpoint-up(lg) {
        min-width: $sidebar-width-lg;
      }
      @include media-breakpoint-up(xl) {
        min-width: $sidebar-width-xl;
      }
    }

    &--main {
      @include media-breakpoint-up(lg) {
        min-width: calc(100% - $sidebar-width-lg);
      }
      @include media-breakpoint-up(xl) {
        min-width: calc(100% - $sidebar-width-xl);
      }

      position: relative;

      .ddi-flip-vertical-down {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        margin-top: -10px;
        font-size: 18px;
        @include clickable-gray-color-states;
        background-color: var(--color-background-header);

        &.rotate-180 {
          margin-top: -9px;
          transform: translateX(-50%) rotate(180deg);
        }
      }

      &__last-entity-group {
        margin-top: 28px;
      }

      .archived-callout {
        padding-top: 20px;
        display: flex;
        justify-content: center;
      }
    }
  }
}
</style>
